import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { BlockType, IDIncomeVerificationStatus, VerificationFailureAction } from '@common/entities';
import { VerificationFieldState } from 'state/slices/idIncomeVerificationSlice';
import { RootState } from 'state/store';

import { VALIDATION_MESSAGES } from 'utils/validation/constants';

/**
 * Custom hook to manage verification card visibility and data fetching on the summary page
 *
 * This hook determines:
 * 1. Whether the verification section should be shown at all
 * 2. Which individual verification cards (ID/Income) should be displayed
 * 3. Whether to fetch verification status from the backend
 *
 * Visibility Rules:
 * - Verification section is hidden if no verification status fields are available in the form
 * - Verification section is hidden for lead profiles or preview mode
 * - Individual cards are only shown if their respective status field exists in the form
 *
 * The hook reads verification status values from form fields using BlockName.IDVerification-status
 * and BlockName.IncomeVerification-status field names.
 *
 * @returns Object containing:
 *   - showVerificationCardOnSummaryPage: Whether to show the entire verification section
 *   - showVerificationCards: Object with flags for individual ID and Income cards
 *   - shouldWeFetchVerificationStatus: Whether to fetch verification status from the backend
 */
export const useVerificationUtils = () => {
  // Retrieve authentication and verification state from Redux
  const isLeadProfile = useSelector((state: RootState) => state?.auth?.isLeadProfile ?? false);
  const isPreviewMode = useSelector((state: RootState) => state?.auth?.isPreviewMode ?? false);
  const { idVerification, incomeVerification, isCompletionAllowed } = useSelector(
    (state: RootState) => state?.idIncomeVerification ?? {},
  );

  const { getValues } = useFormContext();
  const isIdVerificationFieldAvailable =
    getValues(`${BlockType.IDVerification}.status`) !== undefined;
  const isIncomeVerificationFieldAvailable =
    getValues(`${BlockType.IncomeVerification}.status`) !== undefined;

  /**
   * Determines if the entire verification section should be visible
   *
   * The section is shown only when:
   * 1. At least one verification status field exists in the form (getValues for each block's `.status` is not undefined)
   * 2. User is not a lead profile
   * 3. User is not in preview mode
   */
  const showVerificationCardOnSummaryPage = useMemo(() => {
    const hasFieldsAvailable = isIdVerificationFieldAvailable || isIncomeVerificationFieldAvailable;

    const isUserAllowed = !isLeadProfile && !isPreviewMode;
    return hasFieldsAvailable && isUserAllowed;
  }, [
    isIdVerificationFieldAvailable,
    isIncomeVerificationFieldAvailable,
    isLeadProfile,
    isPreviewMode,
  ]);

  /**
   * Determines which individual verification cards should be displayed
   *
   * Each card is shown only when its status field exists in the form (getValues for that block's `.status` is not undefined).
   */
  const showVerificationCards = useMemo(
    () => ({
      showIdVerificationCard: isIdVerificationFieldAvailable,
      showIncomeVerificationCard: isIncomeVerificationFieldAvailable,
    }),
    [isIdVerificationFieldAvailable, isIncomeVerificationFieldAvailable],
  );

  /**
   * Determines if verification status should be fetched from the backend
   *
   * Verification status is fetched only when ALL of the following conditions are met:
   * 1. User is not in lead profile (isLeadProfile === false)
   * 2. User is not in preview mode (isPreviewMode === false)
   * 3. The corresponding verification status field exists in the form (getValues for that block's `.status` is not undefined)
   * 4. That verification has status "InProgress" in Redux state
   *
   * @returns true if verification status should be fetched, false otherwise
   */
  const shouldWeFetchVerificationStatus = useMemo(() => {
    if (isLeadProfile || isPreviewMode) return false;
    if (!isIdVerificationFieldAvailable && !isIncomeVerificationFieldAvailable) return false;
    if (
      isIdVerificationFieldAvailable &&
      idVerification.status === IDIncomeVerificationStatus.InProgress
    )
      return true;
    if (
      isIncomeVerificationFieldAvailable &&
      incomeVerification.status === IDIncomeVerificationStatus.InProgress
    )
      return true;
    return false;
  }, [
    isIdVerificationFieldAvailable,
    isIncomeVerificationFieldAvailable,
    idVerification?.status,
    incomeVerification?.status,
    isLeadProfile,
    isPreviewMode,
  ]);

  const waitForVerificationToComplete = (data: VerificationFieldState) => {
    const { isRequired, status, verificationFailureAction } = data;

    if (status === IDIncomeVerificationStatus.Success || status === null) return false;

    const isRestrictiveAction =
      verificationFailureAction === VerificationFailureAction.ApplicationCantBeCompleted ||
      verificationFailureAction === VerificationFailureAction.LockdownTheApplication;

    if (isRequired) {
      if (status === IDIncomeVerificationStatus.ReInitiated || status === null) return true;
      if (verificationFailureAction === VerificationFailureAction.DoNothing) return false;
      return true;
    }

    if (status === IDIncomeVerificationStatus.InProgress && isRestrictiveAction) return true;
    return false;
  };

  const showBanner = useMemo(() => {
    if (!isCompletionAllowed)
      return {
        showBanner: true,
        bannerMessage: VALIDATION_MESSAGES.VERIFICATION_FAILED_APPLICATION_CANT_BE_COMPLETED,
      };

    if (
      (isIdVerificationFieldAvailable && waitForVerificationToComplete(idVerification)) ||
      (isIncomeVerificationFieldAvailable && waitForVerificationToComplete(incomeVerification))
    )
      return {
        showBanner: true,
        bannerMessage: VALIDATION_MESSAGES.WAIT_FOR_VERIFICATION_TO_COMPLETE,
      };

    return {
      showBanner: false,
      bannerMessage: '',
    };
  }, [
    isIdVerificationFieldAvailable,
    isIncomeVerificationFieldAvailable,
    isCompletionAllowed,
    idVerification,
    incomeVerification,
  ]);

  return {
    showVerificationCardOnSummaryPage,
    showVerificationCards,
    shouldWeFetchVerificationStatus,
    showBanner,
  };
};

/**
 * Alias export for backward compatibility
 * @deprecated Use useVerificationUtils instead
 */
export const useShowVerificationSection = useVerificationUtils;
